import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, Flex, Text, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { Sizes } from "@jsq/ds/styles";
import { AddressType, InstitutionAccountTypeEnum, TaxIdType } from "~/graphql/supergraphEnums";
import {
  useCreateInstitutionProileMutation,
  useLinkBeneficialOwnerMutation,
  useLinkControllingPersonMutation,
  useUpdatePicrPositionDetailsMutation,
} from "~/graphql/supergraphHooks";
import { type ComplianceInstitutionProfileInput } from "~/graphql/supergraphTypes";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { resolveAmlEntityId } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { toISOFormat } from "~/utils/datetime";
import { type RoleState } from "../../../common/AutoProfileCreationCard";
import EntityFormFields from "../../../common/EntityFields";
import { VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE } from "../../../common/EntityFields/constants";
import { buildRelationshipTypes } from "../../../common/ProfileActions/utils";
import { BackButtonWithinForm } from "../BackButtonWithinForm";
import { linkOwnerControllerRoles } from "../utils/linkOwnerControllerRoles";
import { OWNERSHIP_CAP_EXCEEDED_MESSAGE, wouldExceedOwnershipCap } from "../utils/ownershipCap";
import { useExistingOwnershipTotal } from "../utils/useExistingOwnershipTotal";
import { FIELD_MAPPING, INITIAL_VALUES } from "./constants";
import ProfileEntityFormFields from "./ProfileEntityFormFields";
import { getSchema } from "./schema";
import { createEntityForm } from "./styles_vanilla.css";
import { FormValues } from "./types";

interface Props {
  caseId: number;
  parentProfileId: number | undefined;
  inAiExtractionMode?: boolean;
  positionId?: string | null;
  isBeneficiaryProfile?: boolean;
  isOnboardingCase?: boolean;
  saveButtonLabel?: string;
  backNavigationLabel?: string;
  navigateOnCompletion: (profileId: number) => void;
  navigateOnCancel: () => void;
  extractedData?: Record<string, string> | null;
  initialRoleValues?: RoleState;
}

const CreateEntityForm = ({
  caseId,
  parentProfileId,
  positionId,
  inAiExtractionMode = false,
  isBeneficiaryProfile = false,
  isOnboardingCase = false,
  saveButtonLabel = "Save and exit",
  backNavigationLabel,
  navigateOnCompletion,
  navigateOnCancel,
  extractedData,
  initialRoleValues,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { aiExtractionSupported, arenaAccessId } = useCurrentCaseContext();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const existingOwnershipTotal = useExistingOwnershipTotal(caseId, parentProfileId);
  const [createInstitutionProfile] = useCreateInstitutionProileMutation({
    fetchPolicy: "network-only",
    refetchQueries: ["GetCaseInvestingProfileRiskRating", "GetPositions"],
    onError: (e) => toast.error(e.message),
  });

  const [linkBeneficialOwner] = useLinkBeneficialOwnerMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
  });

  const [linkControllingPerson] = useLinkControllingPersonMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
  });

  const [updatePicrPositionDetails] = useUpdatePicrPositionDetailsMutation({
    refetchQueries: ["GetPicrPositionDetails"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  async function addNewInstitution(
    data: ComplianceInstitutionProfileInput,
    roleValues: Pick<FormValues, "isOwner" | "isController" | "ownershipPercentage">,
  ) {
    if (
      roleValues.isOwner &&
      wouldExceedOwnershipCap(existingOwnershipTotal, roleValues.ownershipPercentage)
    ) {
      toast.error(OWNERSHIP_CAP_EXCEEDED_MESSAGE);
      return;
    }
    try {
      const newProfileResponse = await createInstitutionProfile({
        variables: {
          arenaAccessId: arenaAccessId ?? 0,
          data: data,
          parentProfileId: parentProfileId,
        },
      });
      const profileId = newProfileResponse?.data?.createInstitutionProfile?.id;
      if (newProfileResponse?.errors || !profileId) {
        toast.error("Failed to create institution profile");
        return;
      }

      if (isOnboardingCase && parentProfileId) {
        await linkOwnerControllerRoles({
          parentProfileId,
          childProfileId: profileId,
          isOwner: roleValues.isOwner,
          isController: roleValues.isController,
          ownershipPercentage: roleValues.ownershipPercentage,
          linkBeneficialOwner,
          linkControllingPerson,
        });
      }

      if (positionId) {
        await updatePicrPositionDetails({
          variables: {
            data: {
              caseId: caseId,
              positionId: positionId,
              createdProfileId: profileId,
            },
          },
        });
      }

      toast.success("New profile created successfully");
      navigateOnCompletion(profileId);
    } catch {
      // errors already toasted via onError
    }
  }

  const handleSubmit = async (values: Partial<FormValues>) => {
    const profileValues = {
      taxId: values.taxId,
      caseId: caseId,
      addresses: [
        {
          addressType: AddressType.Unknown,
          street: values.streetLine1,
          street2: values.streetLine2,
          city: values.city,
          state: values.stateOrProvince,
          postalCode: values.zipCode,
          country: values.country || "US",
        },
      ],
    };
    const trimmedDateOfFormation = values.dateOfFormation
      ? toISOFormat(values.dateOfFormation)
      : undefined;
    const roles =
      isBeneficiaryProfile || isOnboardingCase ? undefined : buildRelationshipTypes(values);

    const entityValues = {
      accountType:
        VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE[values.entityType || InstitutionAccountTypeEnum.Llc],
      legalBusinessName: values.entityName,
      dateOfFormation: trimmedDateOfFormation,
      taxIdType: TaxIdType.Ein,
      ownershipPercentage: values.ownershipPercentage,
      relationshipType: roles,
      amlEntityId: resolveAmlEntityId(
        values.amlEntityType,
        isOnboardingCase || ioReusableProfilesEnabled,
      ),
      ...(isOnboardingCase ? { registrationNumber: values.registrationNumber } : {}),
    };

    const data = {
      ...profileValues,
      ...entityValues,
    };
    await addNewInstitution(
      // @ts-expect-error: data may be missing required fields
      data,
      {
        isOwner: Boolean(values.isOwner),
        isController: Boolean(values.isController),
        ownershipPercentage: values.ownershipPercentage ?? 0,
      },
    );
  };

  const closeForm = () => {
    history.push({
      pathname: `${location.pathname.split("/entity")[0]}`,
      search: location.search,
    });
  };

  const includeRoleValidation = !(positionId || isBeneficiaryProfile);
  const isAIPrefillFlow = Boolean(extractedData);
  const validationSchema = useMemo(
    () => getSchema(includeRoleValidation, isOnboardingCase || ioReusableProfilesEnabled),
    [includeRoleValidation, isOnboardingCase, ioReusableProfilesEnabled],
  );

  const mergedInitialValues = useMemo(() => {
    const merged: FormValues = { ...INITIAL_VALUES };

    if (initialRoleValues) {
      merged.isOwner = initialRoleValues.isOwner;
      merged.isController = initialRoleValues.isController;
      merged.ownershipPercentage = initialRoleValues.ownershipPercentage;
    }

    (Object.keys(INITIAL_VALUES) as Array<keyof FormValues>).forEach((fieldKey) => {
      const extractedKey = FIELD_MAPPING[fieldKey];
      const extractedValue = extractedKey && extractedData?.[extractedKey];
      if (extractedValue !== undefined && extractedValue !== null && extractedValue !== "") {
        (merged[fieldKey] as typeof extractedValue) = extractedValue;
      }
    });

    return merged;
  }, [extractedData, initialRoleValues]);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      submitOnlyChangedValues={!isAIPrefillFlow}
    >
      <Column size="lg">
        <Card className={createEntityForm}>
          {(inAiExtractionMode || backNavigationLabel) && (
            <BackButtonWithinForm
              onClick={isOnboardingCase ? navigateOnCancel : closeForm}
              displayText={backNavigationLabel || "Select individual or entity"}
            />
          )}
          <Text marginBottom="md" variant="h3">
            New entity
          </Text>
          {!(positionId || isBeneficiaryProfile) && (
            <ProfileEntityFormFields isOnboardingCase={isOnboardingCase} />
          )}
          <EntityFormFields
            inAiExtractionMode={inAiExtractionMode}
            aiExtractionSupported={aiExtractionSupported}
            showOnboardingOwnerControllerFields={isOnboardingCase}
          />
        </Card>
        <Flex justifyContent="flex-end" gap={Sizes.MD}>
          <CancelButton onClick={navigateOnCancel}>Back</CancelButton>
          <SubmitButton>{saveButtonLabel}</SubmitButton>
        </Flex>
      </Column>
    </Form>
  );
};

export default CreateEntityForm;
